<template>
  <div class="EditMyAccount">
    <MainPageHeader/>
    <PageContent>
      <AccountForms
          v-if="account"
          :account="account"
          :addressesEditModes="addressesEditModes"
          :passwordEditable="passwordEditable"
          :isLoadingAccount="isLoadingAccount"
          :isLoadingPassword="isLoadingPassword"
          :isLoadingInvoice="isLoadingInvoice"
          :isShippingEmpty="isShippingEmpty"
          :isInvoiceEmpty="isBillingEmpty"
          @editAddress="editAddress"
          @saveChanges="saveChanges"
          @editPassword="editPassword"
          @savePassword="savePassword"
          @delete="deleteAccount"
          @invoiceTick="handleDoWantInvoiceTick"
      />
    </PageContent>
    <PageNotFound v-if="loadError" :message="loadError"/>
    <LoadingEditMyAccount v-if="!account && !loadError"/>
    <Footer/>
  </div>
</template>

<script>
import MainPageHeader from '../components/MainPageHeader.vue'
import PageContent from '../components/generic/PageContent.vue'
import AccountForms from '../components/accountView/AccountForms.vue'
import PageNotFound from '../components/PageNotFound.vue'
import LoadingEditMyAccount from '../components/LoadingEditMyAccount'
import { cancelAddressEditing } from '../helpers/accountEditHelper'
import { getAccountService } from '../services/accountService'
import { getAuthService } from '../services/authService'
import { SUBUSER, USER } from '../types/role.types'
import { limitView } from '../helpers/permissionHelper'
import '../types/account.types'
import Footer from '@/components/Footer'

export default {
  name: 'EditMyAccount',
  components: {
    Footer,
    MainPageHeader,
    PageContent,
    AccountForms,
    PageNotFound,
    LoadingEditMyAccount,
  },
  data () {
    return {
      copyAccount: {},
      account: null,
      loadError: null,
      addressesEditModes: {},
      passwordEditable: false,
      isLoadingAccount: false,
      isLoadingPassword: false,
      isLoadingInvoice: false,
      isShippingEmpty: false,
      isBillingEmpty: false,
    }
  },
  methods: {
    changeEditModes (index) {
      this.addressesEditModes[index] = !this.addressesEditModes[index]
    },
    async editAddress (index) {
      const accountKey = await getAuthService()
          .getLoggedInKey()
      const accountChanges = await cancelAddressEditing(accountKey, this.addressesEditModes)

      if (accountChanges) {
        this.account = JSON.parse(JSON.stringify(this.copyAccount))
      }
      this.changeEditModes(index)
      this.passwordEditable = false

    },
    async saveChanges (dataType) {
      let status

      if (dataType === 'shippingAddress') {
        this.isLoadingAccount = true
        status = await getAccountService()
            .updateShippingAddress(this.account)
        this.isShippingEmpty = false
      } else {
        this.isLoadingInvoice = true
        this.account.billingAddress.invoiceWanted = true
        status = await getAccountService()
            .updateBillingAddress(this.account)
        this.isBillingEmpty = false
      }

      if (status) {
        this.copyAccount = JSON.parse(JSON.stringify(this.account))
        this.changeEditModes(dataType)
      }
      this.isLoadingAccount = false
      this.isLoadingInvoice = false
    },
    async handleDoWantInvoiceTick(doWantInvoice) {
      this.account.billingAddress.invoiceWanted = doWantInvoice
      await getAccountService()
          .updateBillingAddress(this.account)
    },
    async editPassword () {
      this.passwordEditable = !this.passwordEditable

      const accountKey = await getAuthService()
          .getLoggedInKey()
      const accountChanges = await cancelAddressEditing(accountKey, this.addressesEditModes)
      if (accountChanges) {
        this.account = {
          ...this.account,
          ...accountChanges,
        }
      }
      this.addressesEditModes = {}
    },
    async savePassword ({
      currentPassword,
      newPassword,
    }) {
      this.isLoadingPassword = true
      await getAccountService()
          .updatePassword(
              await getAuthService()
                  .getLoggedInKey(),
              currentPassword,
              newPassword,
          ).then(status => {
            if(status === 422) {
              this.$root.showSnackbar("Błąd podczas zmiany hasła.", "error")
            } else {
              this.$root.showSnackbar("Pomyślnie zmieniono hasło.", "success")
            }
          })

      this.passwordEditable = false
      this.isLoadingPassword = false
    },
    async deleteAccount () {
      const result = await getAccountService()
          .deleteAccount(await getAuthService()
              .getLoggedInKey())
      if (result) {
        this.$router.push({
          name: 'account-list',
        })
      }
    },
  },

  async mounted () {
    window.scrollTo(0,0);
    (async () => {
      const [
        loggedInEmail,
      ] = await Promise.all([
        getAuthService().getLoggedInEmail(),
      ])

      this.loggedInEmail = loggedInEmail
      if (this.loggedInEmail == null) {
        this.$router.replace({
          name: 'logInPage',
          params: {fromPage: 'edit-my-account'}
        })
      } else {
        if (await limitView(this.$router, [USER, SUBUSER])) {
          return
        }

        this.account = await getAccountService()
            .getAccountWithAddresses(await getAuthService()
                .getLoggedInKey())

        this.copyAccount = JSON.parse(JSON.stringify(this.account))

        if (this.account.shippingAddress.city.length === 0) {
          this.addressesEditModes = {
            ['shippingAddress']: true
          }
          this.isShippingEmpty = true
        } else {
          this.addressesEditModes = {
            ['shippingAddress']: false
          }
        }

        if (this.account.billingAddress.city.length === 0) {
          this.addressesEditModes = {
            ...this.addressesEditModes,
            ['billingAddress']: true
          }
          this.isBillingEmpty = true
        } else {
          this.addressesEditModes = {
            ...this.addressesEditModes,
            ['billingAddress']: false
          }
        }
        if (this.account === null) {
          this.loadError = 'The account with the specified id doesn\'t exist'
        }
      }
    })()
    
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_variables.scss";

.EditMyAccount {
  position: relative;
  padding-bottom: 20px;
  min-height: calc(100vh - 380px);
}
</style>
